import { Link, useLocation } from "react-router-dom";
import { Gift, LayoutDashboard, Settings2, Trophy, ReceiptText, Dices, ChevronRight, ExternalLink, QrCode, HandHeart } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { basarStatusMeta, basarUrl, dashboardBasarUrl } from "../../lib/basar";
import { useLang } from "../../context/LanguageContext";

// Topp + undermeny for Basar-modulen i dashbordet. Utan `basar`: berre modultittel. Med `basar`: brødsmule + Oversikt · Oppsett · Premiar · Sal · Trekning.
export const BasarNav = ({ basar, actions }) => {
  const { t } = useLang();
  const { pathname } = useLocation();
  const items = basar
    ? [
        { to: dashboardBasarUrl(basar.id), label: t("Oversikt"), Icon: LayoutDashboard, active: pathname === dashboardBasarUrl(basar.id), testId: "basar-nav-overview" },
        { to: dashboardBasarUrl(basar.id, "oppsett"), label: t("Oppsett"), Icon: Settings2, active: pathname.endsWith("/oppsett"), testId: "basar-nav-setup" },
        { to: dashboardBasarUrl(basar.id, "premiar"), label: t("Premier"), Icon: Gift, active: pathname.endsWith("/premiar"), testId: "basar-nav-prizes", count: basar.prizes?.length || 0 },
        { to: dashboardBasarUrl(basar.id, "gaver"), label: t("Gaver"), Icon: HandHeart, active: pathname.endsWith("/gaver"), testId: "basar-nav-gifts", count: basar.metrics?.gifts || 0 },
        { to: dashboardBasarUrl(basar.id, "sal"), label: t("Salg"), Icon: ReceiptText, active: pathname.endsWith("/sal"), testId: "basar-nav-sales", count: basar.metrics?.orders || 0 },
        { to: dashboardBasarUrl(basar.id, "trekning"), label: t("Trekning"), Icon: Dices, active: pathname.endsWith("/trekning"), testId: "basar-nav-draw" },
        { to: dashboardBasarUrl(basar.id, "kontroll"), label: t("Utlevering"), Icon: QrCode, active: pathname.includes("/kontroll"), testId: "basar-nav-check" },
      ]
    : [];
  const st = basar ? basarStatusMeta(basar) : null;
  return (
    <div className="space-y-4" data-testid="basar-nav">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-2 min-w-0">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] shrink-0"><Gift className="w-5 h-5" /></span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {basar ? (
                <nav aria-label={t("Brødsmuler")} className="flex items-center gap-1 text-sm text-muted-foreground min-w-0">
                  <Link to="/dashboard/basar" className="hover:text-foreground transition-colors duration-150" data-testid="basar-nav-all-link">{t("Basar")}</Link>
                  <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                </nav>
              ) : (
                <h1 className="text-xl sm:text-2xl font-heading font-semibold tracking-tight">{t("Basar")}</h1>
              )}
              <Badge variant="outline" className="text-[10px] uppercase tracking-wide text-[hsl(var(--info))] border-[hsl(var(--info))]/40" data-testid="basar-module-kicker">{t("Tilleggsmodul")}</Badge>
            </div>
            {basar && (
              <div className="flex items-center gap-2 flex-wrap mt-0.5">
                <h1 className="text-xl sm:text-2xl font-heading font-semibold tracking-tight truncate" data-testid="basar-nav-title">{basar.name}</h1>
                <Badge className={st.className} data-testid="basar-nav-status">{st.label}</Badge>
                {basar.status !== "draft" && (
                  <Button asChild variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground" data-testid="basar-nav-public-link">
                    <a href={basarUrl(basar.id)} target="_blank" rel="noreferrer"><ExternalLink className="w-3.5 h-3.5 mr-1" /> {t("Se offentlig side")}</a>
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
      {items.length > 0 && (
        <nav className="flex flex-wrap items-center gap-1 rounded-xl border border-border bg-card p-1" aria-label={t("Basar")} data-testid="basar-subnav">
          {items.map(({ to, label, Icon, active, testId, count }) => (
            <Link
              key={to}
              to={to}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors duration-150 ${active ? "bg-[hsl(var(--accent))] text-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}
              aria-current={active ? "page" : undefined}
              data-testid={testId}
            >
              <Icon className="w-4 h-4" />
              {label}
              {count > 0 && <span className="ml-0.5 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-secondary text-secondary-foreground text-[11px] font-semibold tabular-nums" data-testid={`${testId}-count`}>{count}</span>}
            </Link>
          ))}
        </nav>
      )}
    </div>
  );
};

/** Lite metrikk-kort (dashbord) med ikon-boble i basar-fargen */
export const BasarMetric = ({ icon: Icon, label, value, hint, testId, children }) => (
  <div className="rounded-xl border border-border bg-card p-4 flex items-start gap-3" data-testid={testId}>
    <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] shrink-0"><Icon className="w-4 h-4" /></span>
    <div className="min-w-0 flex-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-heading font-semibold tabular-nums leading-tight truncate">{value}</p>
      {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
      {children}
    </div>
  </div>
);
